import { Search } from "lucide-react";

import type { SmileSimulationController } from "../hooks/useSmileSimulationController";
import { formatSmileSimulationDateTime } from "../lib/session";
import { getSmileSimulationCopy } from "../model/i18n";
import type { SmileSimulationLanguage } from "../model/types";
import { SmileSimulationEmptyState } from "./SmileSimulationEmptyState";

type SmileSimulationHistoryListPageProps = {
  clinic: SmileSimulationController["clinic"];
  history: SmileSimulationController["history"];
  isExperience?: boolean;
  language: SmileSimulationLanguage;
  onOpenPatient: SmileSimulationController["actions"]["openPatientDetail"];
  onQueryChange: SmileSimulationController["actions"]["updateHistoryQuery"];
  onStartNewSimulation: SmileSimulationController["actions"]["startNewSimulation"];
};

export function SmileSimulationHistoryListPage({
  clinic,
  history,
  isExperience = false,
  language,
  onOpenPatient,
  onQueryChange,
  onStartNewSimulation,
}: SmileSimulationHistoryListPageProps) {
  const copy = getSmileSimulationCopy(language);

  return (
    <section className="flex h-full min-h-0 w-full flex-1 flex-col gap-4 sm:gap-6">
      <div className="space-y-2 text-center">
        <p className="text-sm font-semibold uppercase tracking-[0.16em] text-[#8F14E5]/70">
          {isExperience ? "Experience History" : "Patient History"}
        </p>
        <h2 className="text-2xl font-semibold text-[#1f1230] sm:text-[1.75rem]">
          {isExperience ? "참가자 기록 보기" : copy.history.title}
        </h2>
        {!isExperience ? (
          <p className="text-sm leading-6 text-[#5f3a78]">
            {clinic.email} {copy.history.descriptionSuffix}
          </p>
        ) : null}
      </div>

      <div className="flex flex-col gap-3 sm:flex-row">
        {!isExperience ? (
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8F14E5]/60" />
            <input
              type="search"
              value={history.query}
              onChange={(event) => onQueryChange(event.target.value)}
              placeholder={copy.history.searchPlaceholder}
              className="h-11 w-full rounded-full border border-white/70 bg-white/90 pl-10 pr-4 text-sm text-[#1f1230] shadow-[0_14px_35px_rgba(17,17,17,0.06)] outline-none ring-0 placeholder:text-[#907e9f] focus:border-[#8F14E5]/40"
            />
          </div>
        ) : null}
      </div>

      {history.hasRecords ? (
        history.patients.length > 0 ? (
          <div className="grid gap-3 overflow-y-auto pb-2">
            {history.patients.map((patient) => (
              <button
                key={patient.id}
                type="button"
                onClick={() => onOpenPatient(patient.id)}
                className="rounded-[1.6rem] border border-white/70 bg-white/90 p-4 text-left shadow-[0_16px_40px_rgba(17,17,17,0.08)] backdrop-blur-sm transition hover:bg-white"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    {!isExperience ? (
                      <>
                        <p className="text-base font-semibold text-[#1f1230]">
                          {patient.name}
                        </p>
                        <p className="mt-1 text-sm text-[#6b577d]">
                          Chart No. {patient.chartNumber}
                        </p>
                      </>
                    ) : (
                      <p className="text-base font-semibold text-[#1f1230]">
                        {patient.chartNumber}
                      </p>
                    )}
                    <p className="mt-3 text-sm text-[#6b577d]">
                      {copy.history.latestRecord}{" "}
                      {patient.latestRecord
                        ? formatSmileSimulationDateTime(
                            patient.latestRecord.createdAt,
                          )
                        : "-"}
                    </p>
                  </div>
                  {!isExperience ? (
                    <div className="rounded-full bg-[#f3e8ff] px-3 py-1 text-xs font-semibold text-[#7c3aed]">
                      {patient.recordCount}
                      {language === "ko" ? "" : " "}
                      {copy.home.recordUnit}
                    </div>
                  ) : null}
                </div>
                {patient.latestRecord && !isExperience ? (
                  <div className="mt-4 flex gap-2">
                    <img
                      src={patient.latestRecord.captureImage}
                      alt={`${patient.name} capture preview`}
                      className="h-16 w-16 rounded-2xl object-cover"
                    />
                    <img
                      src={patient.latestRecord.resultImage}
                      alt={`${patient.name} result preview`}
                      className="h-16 w-16 rounded-2xl object-cover"
                    />
                  </div>
                ) : null}
              </button>
            ))}
          </div>
        ) : (
          <SmileSimulationEmptyState
            title={copy.history.noSearchTitle}
            description={
              isExperience
                ? "다른 참가자명 또는 참가 코드로 검색해 보세요."
                : copy.history.noSearchDescription
            }
          />
        )
      ) : (
        <SmileSimulationEmptyState
          title={
            isExperience ? "아직 참가자 기록이 없어요" : copy.history.emptyTitle
          }
          description={
            isExperience
              ? "새 스마일 시뮬레이션을 완료하면 참가자 기록이 여기에 표시됩니다."
              : copy.history.emptyDescription
          }
          actionLabel={copy.history.emptyAction}
          onAction={() => onStartNewSimulation()}
        />
      )}
    </section>
  );
}
